import React, { useCallback, useEffect, useState } from "react";
import DistributorTable from "../../components/table/DistributorTable";
import DistributorApi from "../../utils/api/DistributorApi";
import FixedAgent from "../../utils/api/FixedAgent";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./History.module.css";
import Pagination from "../../components/list/Pagination";
import { fetchWithSearchFallback } from "../../utils/searchFallback";
import useDebouncedValue from "../../hooks/useDebouncedValue";

function ExportedPins() {
  const [allTransactions, setAllTransactions] = useState(null);
  const [filteredTransactions, setFilteredTransactions] =
    useState(allTransactions);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput);
  const [pinId, setPinId] = useState(null);
  const [pin, setPin] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [lastPage, setLastPage] = useState(null);
  const [serverSearchAvailable, setServerSearchAvailable] = useState(null);
  const [usingServerSearch, setUsingServerSearch] = useState(false);

  const token = useSelector((state) => state.userData.loginData.token);

  const accountType = useSelector(
    (state) => state.userData.loginData.account_type
  );

  const clearInvoiceHandler = async (id) => {
    try {
      const response = await (accountType === "DISTRIBUTOR"
        ? DistributorApi
        : FixedAgent
      ).clearInvoice({
        token,
        id,
      });

      toast.success("Sent successfully", {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
      return response;
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
      console.error("Failed to fetch customers", error);
    }
  };

  const fetchCustomers = useCallback(async () => {
    try {
      const result = await fetchWithSearchFallback({
        search: debouncedSearch,
        serverSearchAvailable,
        fetchPage: ({ search }) => (accountType === "DISTRIBUTOR" ? DistributorApi : FixedAgent).newExportedPinsHistory({ token, page: currentPage, search }),
      });
      setServerSearchAvailable(result.serverSearchAvailable);
      setUsingServerSearch(result.usedServerSearch);
      const exported_pins = result.response.data.exported_pins;
      setAllTransactions(exported_pins);
      setLastPage(exported_pins?.last_page ?? null);
      setHasNextPage(!!exported_pins?.next_page_url);
      setHasPrevPage(!!exported_pins?.prev_page_url);
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
      console.error("Failed to fetch customers", error);
    }
  }, [token, accountType, currentPage, debouncedSearch, serverSearchAvailable]);

  useEffect(() => {
    const transactions = allTransactions?.data || allTransactions;
    if (searchInput === "" || usingServerSearch) {
      setFilteredTransactions(transactions);
    } else {
      const filtered = transactions?.filter((customer) =>
        (accountType === "DISTRIBUTOR"
          ? customer?.buyer_details
          : customer?.seller_details
        )?.fullname
          ?.toLowerCase()
          ?.includes(searchInput?.toLocaleLowerCase())
      );
      setFilteredTransactions(filtered);
    }
  }, [searchInput, allTransactions, accountType, usingServerSearch]);

  useEffect(() => {
    const fetchPin = async () => {
      if (pinId) {
        try {
          const response = await (accountType === "DISTRIBUTOR"
            ? DistributorApi
            : FixedAgent
          ).getNewExportedPin({
            token,
            id: pinId.id,
          });
          console.log(response.data);
          setPin(response.data.data[0].pin);
        } catch (error) {
          toast.error(error?.response?.data?.message, {
            position: "top-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "dark",
          });
          console.error("Failed to fetch pin", error);
        }
      }
    };

    fetchPin();
  }, [pinId, token, accountType]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  return (
    <div>
      <ToastContainer />

      <div className={styles.filter_wrapper}>
        <input
          type="text"
          value={searchInput}
          onChange={(e) => { setSearchInput(e.target.value); setUsingServerSearch(false); setCurrentPage(1); }}
          placeholder={serverSearchAvailable === false ? "Filter this page by buyer" : "Search buyer name"}
          className={styles.filter_input}
        />
        <button className={styles.export_button}>Export</button>
      </div>

      <DistributorTable
        tableData={filteredTransactions}
        table_type={"pin"}
        sold={"sold"}
        setPinId={setPinId}
        pin={pin}
        accountType={accountType}
        clearInvoiceHandler={clearInvoiceHandler}
      />

      <Pagination
        pagination={{ page: currentPage, lastPage: lastPage || 1, total: allTransactions?.total || 0, hasNextPage, hasPreviousPage: hasPrevPage }}
        onPageChange={setCurrentPage}
        itemLabel="exported PINs"
      />
    </div>
  );
}

export default ExportedPins;
